import { useEffect, useState } from 'react';

/** Événement non standard (pas encore dans lib.dom.d.ts) déclenché par Chrome/Edge/Android sur les PWA installables. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/**
 * Invite d'installation de l'app (PWA). Chrome/Edge/Android déclenchent
 * `beforeinstallprompt`, capturable une fois puis rejouable à la demande ;
 * Safari/iOS ne le déclenche jamais (pas d'API équivalente — seul un texte
 * d'aide manuel est possible, cf. TodayTab).
 */
export function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    function handler(e: Event) {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    }
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  async function promptInstall() {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
  }

  return { canInstall: Boolean(deferredPrompt), promptInstall };
}

/** Best-effort : iOS Safari n'expose aucune API d'installation, juste Partager → Sur l'écran d'accueil. */
export function isLikelyIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}
